import React, { lazy, Suspense, useEffect } from "react";
import { Switch, Route, Redirect } from "react-router-dom";
import Loading from "../../components/shared-components/Loading";
import { APP_LANDING_PATH, APP_PREFIX_PATH } from "../../configs/AppConfig";
import {
  isCompanyAdminEquivalent,
  isContractAdminEquivalent,
  isDelegationBlockedForRole,
  isSuperUser,
} from "../../utils/roleUtils";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import store from "../../redux/store";
import { LOGOUT } from "../../actions/types";
import { Modal } from "antd";

const LazyCompany = lazy(() => import("./company"));
const LazyUser = lazy(() => import("./user"));
const LazyContractManagement = lazy(() => import("./ContractManagement"));
const LazyContractManagementForm = lazy(() => import("./ContractManagement/Form"));
const LazyAuditorManagement = lazy(() => import("./AuditorManagement"));
const LazyTeamManagement = lazy(() => import("./project"));
const LazyDueDateManagerWithTabs = lazy(() => import("./DueDateManager/DueDateManagerWithTabs"));
const LazyDueDateInstanceView = lazy(() => import("./DueDateManager/InstanceView"));
const LazyDelegation = lazy(() => import("./Delegation"));

const dueDateManagerRouteAllowed = () => isSuperUser(localStorage.getItem("role"));

export const AppViews = ({
  isAuthenticated,
  authLoading,
  setLogoutProvision,
}) => {
  useEffect(() => {
    if (setLogoutProvision) {
      let params = JSON.parse(localStorage.getItem("paramsListData"));
      localStorage.clear();
      sessionStorage.clear();
      if (params)
        localStorage.setItem("paramsListData", JSON.stringify(params));
      var html = (
        <div className="text-left">
          This sesssion is invalid. Please insure following best practices to
          avoid this. <br />
          1. Do not share your credentials with anyone. <br />
          2. Multiple users cannot use same credentials at the same time. <br />
          3. Application can be used in one browser tab at a time.
        </div>
      );
      Modal.warning({
        title: "You have been logged out.",
        content: html,
        onOk() {
          store.dispatch({ type: LOGOUT });
          localStorage.clear();
          localStorage.clear();
          if (params)
            localStorage.setItem("paramsListData", JSON.stringify(params));
          window.location.href = "/auth";
        },
      });
    }
  }, [setLogoutProvision]);

  if (!isAuthenticated && !authLoading) {
    try {
      let params = JSON.parse(localStorage.getItem("paramsListData"));
      if (params) localStorage.setItem("paramsListData", JSON.stringify(params));
    } catch (e) {
      // ignore invalid paramsListData
    }
    window.location.href = "/auth";
    // return <Redirect to='/auth' />;
  }

  const settingsBlockedForCompanyAdmin = () =>
    isCompanyAdminEquivalent(localStorage.getItem("role"));

  /** Contract admin: no Contract Management or CXO Management */
  const contractAdminRestrictedRoute = () =>
    isContractAdminEquivalent(localStorage.getItem("role"));

  return (
    <Suspense fallback={<Loading cover="content" />}>
      <Switch>
        <Route
          path={`${APP_PREFIX_PATH}/company-management`}
          render={(routeProps) =>
            settingsBlockedForCompanyAdmin() ? (
              <Redirect to={APP_LANDING_PATH} />
            ) : (
              <Suspense fallback={<Loading cover="content" />}>
                <LazyCompany {...routeProps} />
              </Suspense>
            )
          }
        />
        <Route
          path={`${APP_PREFIX_PATH}/team-management`}
          render={(routeProps) =>
            contractAdminRestrictedRoute() ? (
              <Redirect to={APP_LANDING_PATH} />
            ) : (
              <Suspense fallback={<Loading cover="content" />}>
                <LazyTeamManagement {...routeProps} />
              </Suspense>
            )
          }
        />
        <Route
          path={`${APP_PREFIX_PATH}/user-management`}
          render={(routeProps) =>
            settingsBlockedForCompanyAdmin() ? (
              <Redirect to={APP_LANDING_PATH} />
            ) : (
              <Suspense fallback={<Loading cover="content" />}>
                <LazyUser {...routeProps} />
              </Suspense>
            )
          }
        />
        <Route
          path={`${APP_PREFIX_PATH}/cxo-management`}
          render={(routeProps) =>
            contractAdminRestrictedRoute() ? (
              <Redirect to={APP_LANDING_PATH} />
            ) : (
              <Suspense fallback={<Loading cover="content" />}>
                <LazyAuditorManagement {...routeProps} />
              </Suspense>
            )
          }
        />
        <Route
          path={`${APP_PREFIX_PATH}/contract-management/add`}
          render={(routeProps) =>
            contractAdminRestrictedRoute() ? (
              <Redirect to={APP_LANDING_PATH} />
            ) : (
              <Suspense fallback={<Loading cover="content" />}>
                <LazyContractManagementForm {...routeProps} />
              </Suspense>
            )
          }
        />
        <Route
          path={`${APP_PREFIX_PATH}/contract-management/edit/:id`}
          render={(routeProps) =>
            contractAdminRestrictedRoute() ? (
              <Redirect to={APP_LANDING_PATH} />
            ) : (
              <Suspense fallback={<Loading cover="content" />}>
                <LazyContractManagementForm {...routeProps} />
              </Suspense>
            )
          }
        />
        <Route
          path={`${APP_PREFIX_PATH}/contract-management`}
          render={(routeProps) =>
            contractAdminRestrictedRoute() ? (
              <Redirect to={APP_LANDING_PATH} />
            ) : (
              <Suspense fallback={<Loading cover="content" />}>
                <LazyContractManagement {...routeProps} />
              </Suspense>
            )
          }
        />
        <Route
          path={`${APP_PREFIX_PATH}/obligation-management/add`}
          component={lazy(() => import(`./ObligationManagement/Form`))}
        />
        <Route
          path={`${APP_PREFIX_PATH}/obligation-management/edit/:id`}
          component={lazy(() => import(`./ObligationManagement/Form`))}
        />
        <Route
          path={`${APP_PREFIX_PATH}/obligation-management`}
          component={lazy(() => import(`./ObligationManagement`))}
        />
        <Route
          path={`${APP_PREFIX_PATH}/my-obligation/:type`}
          component={lazy(() => import(`./MyObligation/MyObligationWithTabs`))}
        />
        <Route
          path={`${APP_PREFIX_PATH}/obligation-mapping/select`}
          component={lazy(() => import(`./ObligationMapping/Select`))}
        />
        <Route
          path={`${APP_PREFIX_PATH}/obligation-mapping`}
          exact
          component={lazy(() => import(`./ObligationMapping`))}
        />
        <Route
          path={`${APP_PREFIX_PATH}/team-obligation-user`}
          component={lazy(() => import(`./TeamObligationUser`))}
        />
        <Route
          path={`${APP_PREFIX_PATH}/due-date-manager/instances/:type/:teamObligationId`}
          render={(routeProps) =>
            dueDateManagerRouteAllowed() ? (
              <Suspense fallback={<Loading cover="content" />}>
                <LazyDueDateInstanceView {...routeProps} />
              </Suspense>
            ) : (
              <Redirect to={APP_LANDING_PATH} />
            )
          }
        />
        <Route
          path={`${APP_PREFIX_PATH}/due-date-manager/:type`}
          render={(routeProps) =>
            dueDateManagerRouteAllowed() ? (
              <Suspense fallback={<Loading cover="content" />}>
                <LazyDueDateManagerWithTabs {...routeProps} />
              </Suspense>
            ) : (
              <Redirect to={APP_LANDING_PATH} />
            )
          }
        />
        <Route
          path={`${APP_PREFIX_PATH}/delegation/:type`}
          render={(routeProps) =>
            isDelegationBlockedForRole(localStorage.getItem("role")) ? (
              <Redirect to={APP_LANDING_PATH} />
            ) : (
              <Suspense fallback={<Loading cover="content" />}>
                <LazyDelegation {...routeProps} />
              </Suspense>
            )
          }
        />
        <Route
          path={`${APP_PREFIX_PATH}/instance-obligation`}
          component={lazy(() => import(`./InstanceObligation`))}
        />
        <Route
          path={`${APP_PREFIX_PATH}/compliance-approval`}
          component={lazy(() => import(`./Approvals`))}
        />
        <Route
          path={`${APP_PREFIX_PATH}/auditing-action`}
          component={lazy(() => import(`./Auditing`))}
        />
        {/**
         * GRC is the active dashboard (loaded at the canonical /obligation-overview URL).
         * Classic is kept as a backup at /obligation-overview-classic for side-by-side
         * comparison; the Admin prototype is still reachable at /obligation-overview-admin.
         */}
        <Route
          path={`${APP_PREFIX_PATH}/analytics/obligation-overview-classic`}
          component={lazy(() => import(`./Analytics/ObligationOverview`))}
        />
        <Route
          path={`${APP_PREFIX_PATH}/analytics/obligation-overview-admin`}
          component={lazy(() => import(`./Analytics/ObligationOverviewAdmin`))}
        />
        <Route
          path={`${APP_PREFIX_PATH}/analytics/obligation-overview-grc`}
          component={lazy(() => import(`./Analytics/ObligationOverviewGRC`))}
        />
        <Route
          path={`${APP_PREFIX_PATH}/analytics/obligation-overview`}
          component={lazy(() => import(`./Analytics/ObligationOverviewGRC`))}
        />
        <Route
          path={`${APP_PREFIX_PATH}/analytics/reports/:reportType`}
          component={lazy(() => import(`./Analytics/ReportDetail`))}
        />
        <Route
          path={`${APP_PREFIX_PATH}/analytics/reports`}
          exact
          component={lazy(() => import(`./Analytics/Reports`))}
        />
        <Route
          path={`${APP_PREFIX_PATH}/user-profile`}
          component={lazy(() => import(`./ProfileManage`))}
        />
        <Route>
          <Redirect to={APP_LANDING_PATH} />
        </Route>
      </Switch>
    </Suspense>
  );
};
AppViews.propTypes = {
  authLoading: PropTypes.bool,
  setLogoutProvision: PropTypes.bool,
  isAuthenticated: PropTypes.bool,
};
const mapStateToProps = (state) => ({
  isAuthenticated: state.auth.isAuthenticated,
  authLoading: state.auth.loading,
  setLogoutProvision: state.auth.setLogoutProvision,
});
export default connect(mapStateToProps, {})(AppViews);
